// @flow
import http from 'axios'
import kwanza from './Http'
import { esteroUrl } from './conf'
import { Auth } from './index'

/**
 * EsteroView: A representation of a view func for Estero
 * @typedef { Object } EsteroView
 * @memberof EsteroService
 */
type EsteroView = {
  name: string,
  code: string
}

/**
 * EsteroAction: A representation of a action func for Estero
 * @typedef { Object } EsteroAction
 * @memberof EsteroService
 */
type EsteroAction = {
  name: string,
  code: string
}

/**
 * Get all views available for Estero
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Array<EsteroView>> } The estero views saved to kwanza
 */
const getViews = (): Promise<Array<EsteroView>> => kwanza.getAll('estero_view')

/**
 * Update an existing view or create a new
 * @function
 * @memberof EsteroService
 */
const saveView = (id: string, name: string, version: string, code: string) =>
  kwanza.updateItem('estero_view', id, { name: name, version: version, code: code })

/**
 * Delete a view
 * @function
 * @memberof EsteroService
 */
const deleteView = (id: string) => kwanza.deleteItem('estero_view', id)

/**
 * Get all actions available for Estero
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Array<EsteroAction>> } The estero views saved to kwanza
 */
const getActions = (): Promise<Array<EsteroAction>> => kwanza.getAll('estero_action')

/**
 * Update an existing action or create a new
 * @function
 * @memberof EsteroService
 */
const saveAction = (id: string, name: string, version: string, code: string) =>
  kwanza.updateItem('estero_action', id, { name: name, version: version, code: code })

/**
 * Delete a action
 * @function
 * @memberof EsteroService
 */
const deleteAction = (id: string) => kwanza.deleteItem('estero_action', id)

/**
 * Wrap http requests in this to bail early when the estero url isn't configured
 * or the user isn't a super user
 */
const ifHttpPossible = fn => {
  return !esteroUrl
    ? Promise.reject(Error('No estero url configured'))
    : !Auth.isUserSuper()
    ? Promise.reject(Error('User has no estero privileges'))
    : fn()
}

/**
 * Run a flow on a given Manatee
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Object> } The result of running the flow.
 */
const run = (manatee: string, flow: string, subject: string, args: Object): Promise<Object> => {
  return ifHttpPossible(() =>
    http.post(`${esteroUrl}/manatee/${manatee}/run/flow/${flow}?subject=${subject}`, args)
  )
}

/**
 * Restart a Manatee
 * @function
 * @memberof EsteroService
 */
const restart = (manatee: string): Promise<Object> => {
  return ifHttpPossible(() =>
    http.post(`${esteroUrl}/manatee/${manatee}/run/restart`, null, {
      bypassLogoutInterceptor: true
    })
  )
}

/**
 * Shutdown a Manatee
 * @function
 * @memberof EsteroService
 */
const shutdown = (manatee: string): Promise<Object> => {
  return ifHttpPossible(() =>
    http.post(`${esteroUrl}/manatee/${manatee}/run/shutdown`, {
      bypassLogoutInterceptor: true
    })
  )
}

/**
 * Reset a Manatee
 * @function
 * @memberof EsteroService
 */
const reset = (manatee: string): Promise<Object> => {
  return ifHttpPossible(() =>
    http.post(`${esteroUrl}/manatee/${manatee}/run/reset`, { bypassLogoutInterceptor: true })
  )
}

/**
 * Count number of Manatees
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Int> } How many Manatees currently connected to Estero.
 */
const count = (): Promise<number> => {
  return ifHttpPossible(() => http.get(`${esteroUrl}/manatees`, { bypassLogoutInterceptor: true }))
}

/**
 * Get all available settings
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Array<String>> } The list of all available settings
 */
const availableSettings = (): Promise<Array<String>> => {
  return ifHttpPossible(() =>
    http
      .get(`${esteroUrl}/availablesettings`, { bypassLogoutInterceptor: true })
      .then(res => res.data)
  )
}

/**
 * Inspect a Manatee
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Object> } The inspect object
 */
const inspect = (manatee: string): Promise<Object> => {
  return ifHttpPossible(() =>
    http
      .get(`${esteroUrl}/manatee/${manatee}/inspect`, { bypassLogoutInterceptor: true })
      .then(res => res.data)
  )
}

/**
 * Get the settings for a specific Manatee.
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Object> } The settings object
 */
const getSettings = (manatee: string): Promise<Object> => {
  return ifHttpPossible(() =>
    http
      .get(`${esteroUrl}/manatee/${manatee}/settings`, { bypassLogoutInterceptor: true })
      .then(res => res.data)
  )
}

/**
 * Update a bunch of Manatee settings
 * @function
 * @memberof EsteroService
 *
 * @return { Promise<Object> }
 */
const updateSettings = (manatee: string, settings: Object): Promise<Object> => {
  return ifHttpPossible(() =>
    http
      .post(`${esteroUrl}/manatee/${manatee}/settings`, settings, { bypassLogoutInterceptor: true })
      .then(res => res.data)
  )
}

const mql = {
  operators: {
    '=': {
      keyword: true,
      symbol: '=',
      match: /^([^=!<>~]+)\s*=\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => a + ' = ' + b,
      i18n: 'SEARCH_OPERATOR_EQUALS'
    },

    '!=': {
      keyword: true,
      symbol: '!=',
      match: /^([^=!<>~]+)\s*!=\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => a + ' != ' + b,
      i18n: 'SEARCH_OPERATOR_NOT_EQUALS'
    },
    '<': {
      keyword: true,
      symbol: '<',
      match: /^([^=!<>~]+)\s*<\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => a + ' < ' + b,
      i18n: 'SEARCH_OPERATOR_LESS_THAN'
    },
    '<=': {
      keyword: true,
      symbol: '<=',
      match: /^([^=!<>~]+)\s*<=\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => a + ' <= ' + b,
      i18n: 'SEARCH_OPERATOR_LESS_THAN_OR_EQUALS'
    },
    '>': {
      keyword: true,
      symbol: '>',
      match: /^([^=!<>~]+)\s*>\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => a + ' > ' + b,
      i18n: 'SEARCH_OPERATOR_GREATER_THAN'
    },
    '>=': {
      keyword: true,
      symbol: '>=',
      match: /^([^=!<>~]+)\s*>=\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => a + ' >= ' + b,
      i18n: 'SEARCH_OPERATOR_GREATER_THAN_OR_EQUALS'
    },
    '∋': {
      keyword: true,
      symbol: 'CONTAINS',
      forceStringArg: true,
      match: /^([^=!<>~]+)\s+CONTAINS\s+([^=!<>~]*)$/,
      gen: (a: string, b: string) => `${a} CONTAINS ${b}`,
      i18n: 'SEARCH_OPERATOR_CONTAINS'
    },
    '!∋': {
      keyword: false,
      symbol: '!∋',
      forceStringArg: true,
      match: /^NOT\(([^=!<>~]+)\s+CONTAINS\s+([^=!<>~]+)\)$/,
      gen: (a: string, b: string) => `NOT(${a} CONTAINS ${b})`,
      i18n: 'SEARCH_OPERATOR_CONTAINS_NOT'
    },
    startswith: {
      keyword: true,
      forceStringArg: true,
      symbol: 'STARTSWITH',
      match: /^([^=!<>~]+)\s+STARTSWITH\s+([^=!<>~]+)$/,
      gen: (a: string, b: string) => `${a} STARTSWITH ${b}`,
      i18n: 'SEARCH_OPERATOR_STARTS_WITH'
    },
    endswith: {
      keyword: true,
      symbol: 'endswith',
      forceStringArg: true,
      match: /^([^=!<>~]*)\s+ENDSWITH\s+([^=!<>~]+)$/,
      gen: (a: string, b: string) => `${a} ENDSWITH ${b}`,
      i18n: 'SEARCH_OPERATOR_ENDS_WITH'
    },
    regexp: {
      keyword: true,
      symbol: 'regexp',
      match: /^([^=!<>~]+)\s*~\s*([^=!<>~]+)$/,
      gen: (a: string, b: string) => `${a} ~ ${b}`,
      i18n: 'SEARCH_OPERATOR_MATCHES_REGEXP'
    }
  }
}

const self = {
  getViews,
  saveView,
  deleteView,
  getActions,
  saveAction,
  deleteAction,
  availableSettings,
  getSettings,
  updateSettings,
  inspect,
  run,
  reset,
  shutdown,
  restart,
  count,
  mql
}

/**
 * Estero service: Command and Control for Manatees.
 * @module EsteroService
 * @namespace EsteroService
 * @version 1.0.0
 */
export default self
